import React, { FC } from 'react'
import { CellularPayPrompt } from './types'
import { useDialogStyles } from './styles'
import { daysLabel, daysUntil, formatDate } from './format'
import { isLocked } from './is-locked'
import { payableSimStage } from './sim-status'
import { isRenewalBill } from './owed-description'

const LOCKED_BILL = 'ScreenHub stays locked until this bill is paid.'
const LOCKED_RENEWAL = 'ScreenHub stays locked until this renewal is paid.'

const CONNECTION = 'your cellular connection'

// How long the customer has is the API's to decide, and it says so with the invoice's
// due date and how far past it we already are: the grace period is never a number we
// write down here.
export const lockNotice = (prompt: CellularPayPrompt, now: number = Date.now()): string => {
  // SHA-973 P3: a renewal locks only past its pay-by; it says so as a renewal.
  const LOCKED = !prompt.isBackbill && isRenewalBill(prompt) ? LOCKED_RENEWAL : LOCKED_BILL
  // Once the paywall reports a stage, the stage notice above says what happens
  // next and when; the due-date guess below would contradict it.
  if (payableSimStage(prompt)) return LOCKED
  const due = formatDate(prompt.dueDate)
  if (prompt.daysPastDue > 0) {
    const on = due ? ` (${due})` : ''
    return (
      `${LOCKED} It is already ${daysLabel(prompt.daysPastDue)} past due${on}, ` +
      `so ${CONNECTION} can be switched off at any time until it is paid.`
    )
  }
  const left = daysUntil(prompt.dueDate, now)
  if (!due || left === null) return `${LOCKED} Once it is past due, ${CONNECTION} is switched off.`
  const within = left > 0 ? ` (${daysLabel(left)} from now)` : ''
  return `${LOCKED} If it is not paid by ${due}${within}, ${CONNECTION} is switched off.`
}

// Why the CMS is locked, and what it costs to leave the bill unpaid. Only a locking
// bill (a blocking back-bill) locks anything, so only a locking bill says so.
export const LockNotice: FC<{ prompt: CellularPayPrompt }> = ({ prompt }) => {
  const classes = useDialogStyles()
  if (!isLocked(prompt)) return null
  return (
    <p
      className={classes.mutedCenter}
      data-testid='cellular-lock-notice'
    >
      {lockNotice(prompt)}
    </p>
  )
}
